import { PROJECT_FIELD_LIMITS, Project } from "@amzn/afxtaco-types";
import { apiPost, apiDelete, apiGet, apiPut } from "../apiUtils";

/**
 * Request body for creating a project
 */
export interface CreateProjectRequest {
  projectName?: string;
  projectDescription?: string;
  // Owner alias is derived server-side from the auth token; sending it has no
  // effect. Optional so callers that only need the project row can omit it.
  alias?: string;
  // Salesforce IDs from the start-project form. Stored on the project so they
  // are authoritative from the outset rather than surviving only as prompt text.
  accountId?: string;
  opportunityId?: string;
}

/**
 * Response data for project creation
 */
export interface CreateProjectData {
  message: string;
  project: Project;
}

/**
 * API response wrapper for project creation
 */
export interface CreateProjectResponse {
  success: boolean;
  data: CreateProjectData;
}

/**
 * Creates a new project
 *
 * @param projectId - The unique identifier for the project
 * @param projectData - Project details including name, description, and alias
 * @returns Promise resolving to created project details
 * @throws Error if the API request fails or returns invalid data
 *
 * @example
 * ```typescript
 * const response = await createProject("project-123", {
 *   projectName: "My Project",
 *   projectDescription: "A sample project",
 *   alias: "user-alias"
 * });
 * console.log(response.data.project);
 * ```
 */
export const createProject = async (
  projectId: string,
  projectData: CreateProjectRequest,
): Promise<CreateProjectResponse> => {
  if (projectId.length > PROJECT_FIELD_LIMITS.projectId) {
    throw new Error(
      `Project ID must not exceed ${PROJECT_FIELD_LIMITS.projectId} characters`,
    );
  }
  const restOperation = apiPost(`/projects/${projectId}`, projectData);
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as CreateProjectResponse;
};

/**
 * Response data for project deletion
 */
export interface DeleteProjectData {
  message: string;
}

/**
 * API response wrapper for project deletion
 */
export interface DeleteProjectResponse {
  success: boolean;
  data: DeleteProjectData;
}

/**
 * Deletes an existing project
 *
 * @param projectId - The unique identifier for the project to delete
 * @returns Promise resolving to deletion confirmation
 * @throws Error if the API request fails or project not found
 *
 * @example
 * ```typescript
 * const response = await deleteProject("project-123");
 * console.log(response.data.message); // "Project deleted successfully"
 * ```
 */
export const deleteProject = async (
  projectId: string,
): Promise<DeleteProjectResponse> => {
  const restOperation = apiDelete(`/projects/${projectId}`);
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as DeleteProjectResponse;
};

/**
 * API response wrapper for getting a project
 */
export interface GetProjectResponse {
  success: boolean;
  data: Project;
}

/**
 * Retrieves a specific project by ID
 *
 * @param projectId - The unique identifier for the project to retrieve
 * @returns Promise resolving to project details
 * @throws Error if the API request fails or project not found
 *
 * @example
 * ```typescript
 * const response = await getProject("project-123");
 * console.log(response.data.projectName);
 * ```
 */
export const getProject = async (
  projectId: string,
): Promise<GetProjectResponse> => {
  const restOperation = apiGet(`/projects/${projectId}`);
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as GetProjectResponse;
};

/**
 * Search projects response data structure
 */
export interface SearchProjectsData {
  projects: Project[];
  nextToken?: string;
}

/**
 * API response wrapper for searching projects
 */
export interface SearchProjectsResponse {
  success: boolean;
  data: SearchProjectsData;
  requestId?: string;
}

/**
 * Parameters for searching projects
 */
export interface SearchProjectsParams {
  search?: string;
  nextToken?: string;
  pageSize?: number;
  sortField?: string;
  reverse?: boolean;
}

/**
 * Retrieves a list of projects for the authenticated user with optional search and pagination
 *
 * @param params - Optional search and pagination parameters
 * @returns Promise resolving to list of projects with pagination token
 * @throws Error if the API request fails or returns invalid data
 *
 * @example
 * ```typescript
 * // Get all projects
 * const response = await getProjects();
 * console.log(response.data.projects);
 *
 * // Search projects
 * const searchResponse = await getProjects({
 *   search: "my project"
 * });
 *
 * // With pagination
 * const paginatedResponse = await getProjects({
 *   pageSize: 20,
 *   nextToken: response.data.nextToken
 * });
 * ```
 */
export const getProjects = async (
  params?: SearchProjectsParams,
): Promise<SearchProjectsResponse> => {
  const queryParams: Record<string, string> = {};

  if (params) {
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.nextToken) {
      queryParams.nextToken = params.nextToken;
    }
    if (params.pageSize) {
      queryParams.pageSize = params.pageSize.toString();
    }
    if (params.sortField) {
      queryParams.sortField = params.sortField;
    }
    if (params.reverse !== undefined) {
      queryParams.reverse = params.reverse.toString();
    }
  }

  const restOperation = apiGet("/projects", {
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as SearchProjectsResponse;
};

/**
 * Request body for updating a project
 */
export interface UpdateProjectRequest {
  projectName?: string;
  projectDescription?: string;
  /**
   * Salesforce IDs to link to this project.
   *
   * A supplied ID *replaces* that entity's recorded IDs rather than being added
   * to them, because a project has one customer and this is how the user corrects
   * or repoints it. Scoped per entity: sending only `accountId` leaves a recorded
   * opportunity untouched.
   *
   * Omitting a field is not a way to clear it — there is no unlink today.
   */
  accountId?: string;
  opportunityId?: string;
}

/**
 * Response data for project update
 */
export interface UpdateProjectData {
  message: string;
  project: Project | null;
}

/**
 * API response wrapper for project update
 */
export interface UpdateProjectResponse {
  success: boolean;
  data: UpdateProjectData;
}

/**
 * Updates an existing project
 *
 * @param projectId - The unique identifier for the project to update
 * @param projectData - Project details to update (at least one field required)
 * @returns Promise resolving to updated project details
 * @throws Error if the API request fails or validation fails
 *
 * @example
 * ```typescript
 * // Update project name
 * const response = await updateProject("project-123", {
 *   projectName: "Updated Project Name"
 * });
 *
 * // Update project description
 * const response = await updateProject("project-123", {
 *   projectDescription: "New description"
 * });
 *
 * // Update both
 * const response = await updateProject("project-123", {
 *   projectName: "Updated Name",
 *   projectDescription: "Updated description"
 * });
 * console.log(response.data.project);
 * ```
 */
export const updateProject = async (
  projectId: string,
  projectData: UpdateProjectRequest,
): Promise<UpdateProjectResponse> => {
  const restOperation = apiPut(`/projects/${projectId}`, projectData);
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as UpdateProjectResponse;
};
